import React, { FC, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Accordion, Checkbox } from '@sravni/react-design-system';
import { Heading } from '@sravni/react-design-system/lib/Typography';
import { Controller, SubmitErrorHandler, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Layout } from '../../ui/step';
import { Col, DateInput, onlyRussianOutput, PhoneInput, Rows, SelectInput, TextInput } from '../../../landing.common';
import { FORM_FIELDS, LABELS, MASKS, PAGE, PAGE_CONTEXT, PLACEHOLDERS } from '../../../../constants';
import { scrollToForm } from '../../../../helpers';
import { postMessage, PostMessageType } from '../../../../helpers/whitelable';
import { useSelection } from '../../../../store/reducers/selection';
import {
    additionalPhoneOwnerOptions,
    dependentsOptions,
    educationOptions,
    familyStatusOptions,
} from '../../../../helpers/form.new/sources';
import { FullNameSuggest } from '../../ui/suggest/full_name';
import { StepButtons } from '../../ui/step/components/step.buttons';
import { Categories, EventType } from '../../../../helpers/analytics';
import { Row } from '../common';
import { IUnitCodeSuggest, UnitCodeSuggest } from '../../ui/suggest/unit.code';
import { IssuedSuggest } from '../../ui/suggest/issued';
import { FormAddressSuggest } from '../../ui/suggest/address';
import { useAnalytics } from '../../../hooks/analytics';
import commonStyles from '../common.module.scss';
import { ADDITIONAL_PHONE_OWNER_DEPS } from '../../../../constants/field.dependencies';
import { StepContext } from '../../creator/step.context';
import { useFormData, useFormStore } from '../../../../store/selectors/formStore';
import { useFormOnBlur } from '../../hooks/formHooks';
import { extendFormData, setPageContext } from '../../../../store/reducers/formStore';
import { useEsiaReturnAnalytic } from '../../hooks/esiaReturnAnalytic';
import { api } from '../../../../helpers/client';
import { schema } from './schema';
import styles from './styles.module.scss';
import { AccordionItem } from './components';

export const PassportStepView: FC = React.memo(() => {
    const { page } = useSelection();
    const [issuedOptions, setIssuedOptions] = useState<Array<any>>([]);
    const { headline } = useFormStore();
    const formData = useFormData();
    const dispatch = useDispatch();
    const { pushEvent } = useAnalytics();
    const { nextStep, prevStep, analyticStepText } = useContext(StepContext);

    const form = useForm({
        resolver: yupResolver(schema()),
        mode: 'onSubmit',
        reValidateMode: 'onBlur',
    });

    const {
        control,
        watch,
        formState: { errors, dirtyFields: _, isSubmitting, isValidating },
        trigger,
        reset,
        setValue,
        getValues,
        handleSubmit,
        clearErrors,
    } = form;

    useEsiaReturnAnalytic('Шаг 2|Паспорт');
    useEffect(() => {
        reset({ ...formData }, { keepErrors: true, keepDirtyValues: true });
    }, [formData, reset]);

    const [addressesAreEqual, additionalPhoneOwner] = watch([
        FORM_FIELDS.RESIDENCE_SAME_REGISTRATION,
        FORM_FIELDS.ADDITIONAL_PHONE_OWNER,
    ]);

    useEffect(() => {
        if (!getValues(FORM_FIELDS.RESIDENCE_SAME_REGISTRATION)) {
            return;
        }
        const hasResidenceErrors =
            [FORM_FIELDS.RESIDENCE_STREET, FORM_FIELDS.RESIDENCE_APARTMENT].some((key) => errors[key]) &&
            ![FORM_FIELDS.REGISTRATION_STREET, FORM_FIELDS.REGISTRATION_APARTMENT].some((key) => errors[key]);
        if (hasResidenceErrors) {
            setValue(FORM_FIELDS.RESIDENCE_SAME_REGISTRATION, false);
        }
    }, [errors]);

    useEffect(() => {
        scrollToForm();
        dispatch(setPageContext(PAGE_CONTEXT.STEP_2));
        pushEvent({
            event: EventType.mainEvent,
            eventAction: 'Просмотр экрана|Шаг 2|Паспорт',
        });

        if (page == PAGE.AVITO) {
            postMessage({
                type: PostMessageType.event,
                label: 'broker_form_passport_load',
            });
        }
    }, []);

    const goNext = useCallback(async () => {
        nextStep();
    }, [nextStep]);

    const onSubmitError: SubmitErrorHandler<Record<string, unknown>> = useCallback(
        (e) => {
            const errorKey = Object.keys(e)[0];
            const errorValue = getValues(errorKey);

            pushEvent({
                event: EventType.mainEvent,
                eventAction: 'Невалидная анкета',
                eventLabel: `${analyticStepText}|${errorKey}|Ручной проход|${errorValue}`,
            });
        },
        [analyticStepText],
    );

    const goBack = useCallback(() => {
        pushEvent({
            event: EventType.mainEvent,
            eventCategory: [PAGE.CARDS_RESTORE].includes(page as any) ? Categories.CreditCard : Categories.Loan,
            eventAction: 'Шаг 2|Паспорт|Шаг Назад',
        });
        prevStep();
    }, [prevStep, page, pushEvent]);

    const onUnitCodeBlur = useCallback(
        async (
            event: FE.FieldChangeEvent,
            onBlur: (event: FE.FieldChangeEvent) => void,
            option?: IUnitCodeSuggest,
            optionsForAllCodes?: Array<IUnitCodeSuggest>,
        ) => {
            if (option && optionsForAllCodes) {
                const options = optionsForAllCodes.filter((item) => item.code === option.code);
                const mappedOptions = new Map(options.map((item) => [item.name, item.name]));
                setIssuedOptions(Array.from(mappedOptions.values()));
                setValue(FORM_FIELDS.ISSUED, options?.length === 1 ? options[0].name : '');
            } else if (!(await trigger(FORM_FIELDS.UNIT_CODE))) {
                setIssuedOptions([]);
                setValue(FORM_FIELDS.ISSUED, '');
            }

            onBlur(event);
        },
        [setValue, trigger, setIssuedOptions],
    );

    const additionalPhoneFields = useMemo(
        () => ADDITIONAL_PHONE_OWNER_DEPS[additionalPhoneOwner] || [],
        [additionalPhoneOwner],
    );

    const formOnBlur = useFormOnBlur(form);

    const formOnChange: React.FormEventHandler<HTMLFormElement> = (event) => {
        const { name: fieldName } = event.target as HTMLInputElement;
        if (fieldName && fieldName in errors) {
            clearErrors(fieldName);
        }
    };

    const onSelectFullName: React.ComponentProps<typeof FullNameSuggest>['onSelect'] = useCallback((option) => {
        setValue(FORM_FIELDS.ADDITIONAL_PHONE_OWNER_FIO, option.result);
    }, []);

    const cleanedAddress = async (field: string, value: string) => {
        const address = await api.addressClean(value);

        if (!address) {
            return;
        }

        setValue(field, address.value);

        return address;
    };

    const onSelectRegistrationAddress: React.ComponentProps<typeof FormAddressSuggest>['onSelect'] = useCallback(
        async (option) => {
            const address = await cleanedAddress(FORM_FIELDS.REGISTRATION_STREET, option.value);

            if (!address) {
                return;
            }

            dispatch(
                extendFormData({
                    [FORM_FIELDS.REGISTRATION_DADATA_FULL]: {
                        value: address.value,
                        data: address,
                    },
                }),
            );
        },
        [dispatch, setValue],
    );

    const handleBlurAddress: React.ComponentProps<typeof FormAddressSuggest>['onBlur'] = async (event) => {
        await cleanedAddress(event.target.name, event.target.value);
    };

    const onSelectResidenceAddress: React.ComponentProps<typeof FormAddressSuggest>['onSelect'] = useCallback(
        async (option) => {
            const address = await cleanedAddress(FORM_FIELDS.RESIDENCE_STREET, option.value);

            if (!address) {
                return;
            }

            dispatch(
                extendFormData({
                    [FORM_FIELDS.RESIDENCE_DADATA_FULL]: {
                        value: option.value,
                        data: option.data,
                    },
                }),
            );
        },
        [dispatch],
    );

    const onChangeAddressTheSame = useCallback(
        (onFieldChange: (...events: Array<any>) => void): React.FormEventHandler<HTMLInputElement> => {
            return (event) => {
                const {
                    currentTarget: { checked },
                } = event;
                if (!checked) {
                    dispatch(
                        extendFormData({
                            [FORM_FIELDS.RESIDENCE_SAME_REGISTRATION]: false,
                            [FORM_FIELDS.RESIDENCE_APARTMENT]: '',
                            [FORM_FIELDS.RESIDENCE_STREET]: '',
                            [FORM_FIELDS.RESIDENCE_DADATA_FULL]: null,
                            [FORM_FIELDS.RESIDENCE_ROUTE]: null,
                        }),
                    );
                }
                onFieldChange(event);
            };
        },
        [dispatch],
    );

    return (
        <Layout
            headline={headline}
            onBlur={formOnBlur}
            onSubmit={handleSubmit(goNext, onSubmitError)}
            onChange={formOnChange}
            navigation={true}
            locked={isSubmitting}
            greenMessageIcon="shield"
            greenMessage="Ваши паспортные данные надежно защищены. Мы соблюдаем закон о защите персональных данных."
            step={2}>
            <Accordion defaultIndex={[0, 1, 2]} padding={false}>
                <AccordionItem title={<Heading level={4}>Паспортные данные</Heading>}>
                    <Rows className={styles['item-content']}>
                        <Row>
                            <Col md-4>
                                <Controller
                                    control={control}
                                    name={FORM_FIELDS.BIRTHDAY}
                                    render={({ field }) => (
                                        <DateInput
                                            {...field}
                                            label="Дата рождения"
                                            errorMessage={errors[FORM_FIELDS.BIRTHDAY]?.message}
                                        />
                                    )}
                                />
                            </Col>
                            <Col md-4>
                                <Controller
                                    control={control}
                                    name={FORM_FIELDS.BIRTH_PLACE}
                                    render={({ field }) => (
                                        <TextInput
                                            {...field}
                                            label="Место рождения"
                                            errorMessage={errors[FORM_FIELDS.BIRTH_PLACE]?.message}
                                            onChange={onlyRussianOutput(field)}
                                        />
                                    )}
                                />
                            </Col>
                        </Row>
                        <Row>
                            <Col md-4>
                                <Controller
                                    control={control}
                                    name={FORM_FIELDS.PASSPORT_NUMBER}
                                    render={({ field }) => (
                                        <TextInput
                                            {...field}
                                            label={LABELS[FORM_FIELDS.PASSPORT_NUMBER]}
                                            inputMode="numeric"
                                            mask={MASKS.PASSPORT}
                                            placeholder={PLACEHOLDERS.PASSPORT}
                                            errorMessage={errors[FORM_FIELDS.PASSPORT_NUMBER]?.message}
                                            onChange={field.onChange}
                                        />
                                    )}
                                />
                            </Col>
                            <Col md-4>
                                <Controller
                                    control={control}
                                    name={FORM_FIELDS.PASSPORT_DATE}
                                    render={({ field }) => (
                                        <DateInput
                                            {...field}
                                            label={LABELS[FORM_FIELDS.PASSPORT_DATE]}
                                            errorMessage={errors[FORM_FIELDS.PASSPORT_DATE]?.message}
                                        />
                                    )}
                                />
                            </Col>
                            <Col md-4>
                                <Controller
                                    control={control}
                                    name={FORM_FIELDS.UNIT_CODE}
                                    render={({ field: { onBlur, ...field } }) => (
                                        <UnitCodeSuggest
                                            input={{
                                                ...field,
                                                label: LABELS[FORM_FIELDS.UNIT_CODE],
                                                errorMessage: errors[FORM_FIELDS.UNIT_CODE]?.message,
                                                mask: MASKS.UNIT_CODE,
                                            }}
                                            onBlur={(event, option, allOptions) =>
                                                onUnitCodeBlur(event, onBlur, option, allOptions)
                                            }
                                            onChange={field.onChange}
                                        />
                                    )}
                                />
                            </Col>
                        </Row>
                        <Row>
                            <Col>
                                <Controller
                                    control={control}
                                    name={FORM_FIELDS.ISSUED}
                                    render={({ field }) => (
                                        <IssuedSuggest
                                            input={{
                                                ...field,
                                                label: LABELS[FORM_FIELDS.ISSUED],
                                                errorMessage: errors[FORM_FIELDS.ISSUED]?.message,
                                            }}
                                            options={issuedOptions}
                                            onChange={field.onChange}
                                        />
                                    )}
                                />
                            </Col>
                        </Row>
                    </Rows>
                </AccordionItem>
                <AccordionItem
                    className={styles['accordion-item']}
                    title={<Heading level={4}>Адрес регистрации</Heading>}
                    unmountOnExit={false}>
                    <Rows className={styles['item-content']}>
                        <Row>
                            <Col md-8>
                                <Controller
                                    control={control}
                                    name={FORM_FIELDS.REGISTRATION_STREET}
                                    render={({ field: { onBlur, ...field } }) => (
                                        <FormAddressSuggest
                                            input={{
                                                ...field,
                                                label: LABELS[FORM_FIELDS.REGISTRATION_STREET],
                                                errorMessage: errors[FORM_FIELDS.REGISTRATION_STREET]?.message,
                                            }}
                                            onChange={field.onChange}
                                            onBlur={(event) => {
                                                handleBlurAddress(event);
                                                onBlur();
                                            }}
                                            onSelect={onSelectRegistrationAddress}
                                            toBound="house"
                                        />
                                    )}
                                />
                            </Col>
                            <Col md-4>
                                <Controller
                                    control={control}
                                    name={FORM_FIELDS.REGISTRATION_APARTMENT}
                                    render={({ field }) => (
                                        <TextInput
                                            {...field}
                                            onChange={onlyRussianOutput(field)}
                                            label="Квартира"
                                            errorMessage={errors[FORM_FIELDS.REGISTRATION_APARTMENT]?.message}
                                        />
                                    )}
                                />
                            </Col>
                        </Row>
                        <Row>
                            <Col md-4>
                                <Controller
                                    control={control}
                                    name={FORM_FIELDS.REGISTRATION_DATE}
                                    render={({ field }) => (
                                        <DateInput
                                            {...field}
                                            label={LABELS[FORM_FIELDS.REGISTRATION_DATE]}
                                            errorMessage={errors[FORM_FIELDS.REGISTRATION_DATE]?.message}
                                        />
                                    )}
                                />
                            </Col>
                        </Row>
                        <Row>
                            <Col>
                                <Controller
                                    control={control}
                                    name={FORM_FIELDS.RESIDENCE_SAME_REGISTRATION}
                                    render={({ field: { value, onChange, ...field } }) => (
                                        <Checkbox
                                            checked={value}
                                            {...field}
                                            onChange={onChangeAddressTheSame(onChange)}>
                                            Адрес фактического проживания совпадает с адресом регистрации
                                        </Checkbox>
                                    )}
                                />
                            </Col>
                        </Row>
                        {!addressesAreEqual && (
                            <Row>
                                <Col md-8>
                                    <Controller
                                        control={control}
                                        name={FORM_FIELDS.RESIDENCE_STREET}
                                        render={({ field: { onBlur, ...field } }) => (
                                            <FormAddressSuggest
                                                input={{
                                                    ...field,
                                                    label: LABELS[FORM_FIELDS.RESIDENCE_STREET],
                                                    errorMessage: errors[FORM_FIELDS.RESIDENCE_STREET]?.message,
                                                }}
                                                onChange={field.onChange}
                                                onBlur={(event) => {
                                                    handleBlurAddress(event);
                                                    onBlur();
                                                }}
                                                onSelect={onSelectResidenceAddress}
                                                toBound="house"
                                            />
                                        )}
                                    />
                                </Col>
                                <Col md-4>
                                    <Controller
                                        control={control}
                                        name={FORM_FIELDS.RESIDENCE_APARTMENT}
                                        render={({ field }) => (
                                            <TextInput
                                                {...field}
                                                label="Квартира"
                                                errorMessage={errors[FORM_FIELDS.RESIDENCE_APARTMENT]?.message}
                                            />
                                        )}
                                    />
                                </Col>
                            </Row>
                        )}
                    </Rows>
                </AccordionItem>
                <AccordionItem title={<Heading level={4}>Дополнительная информация</Heading>} unmountOnExit={false}>
                    <Rows className={styles['item-content']}>
                        <Row>
                            <Col md-4>
                                <Controller
                                    control={control}
                                    name={FORM_FIELDS.EDUCATION}
                                    render={({ field }) => (
                                        <SelectInput
                                            {...field}
                                            label={LABELS[FORM_FIELDS.EDUCATION]}
                                            withCrossIcon={false}
                                            options={educationOptions}
                                            errorMessage={errors[FORM_FIELDS.EDUCATION]?.message}
                                        />
                                    )}
                                />
                            </Col>
                            <Col md-4>
                                <Controller
                                    control={control}
                                    name={FORM_FIELDS.FAMILY_STATUS}
                                    render={({ field }) => (
                                        <SelectInput
                                            {...field}
                                            label={LABELS[FORM_FIELDS.FAMILY_STATUS]}
                                            withCrossIcon={false}
                                            options={familyStatusOptions}
                                            errorMessage={errors[FORM_FIELDS.FAMILY_STATUS]?.message}
                                        />
                                    )}
                                />
                            </Col>

                            <Col md-4>
                                <Controller
                                    control={control}
                                    name={FORM_FIELDS.NUMBER_OF_DEPENDENTS}
                                    render={({ field }) => (
                                        <SelectInput
                                            {...field}
                                            label={LABELS[FORM_FIELDS.NUMBER_OF_DEPENDENTS]}
                                            options={dependentsOptions}
                                            errorMessage={errors[FORM_FIELDS.NUMBER_OF_DEPENDENTS]?.message}
                                        />
                                    )}
                                />
                            </Col>
                        </Row>
                        <Row>
                            <Col md-4>
                                <Controller
                                    control={control}
                                    name={FORM_FIELDS.ADDITIONAL_PHONE}
                                    render={({ field }) => (
                                        <PhoneInput
                                            {...field}
                                            label={LABELS[FORM_FIELDS.ADDITIONAL_PHONE]}
                                            errorMessage={errors[FORM_FIELDS.ADDITIONAL_PHONE]?.message}
                                        />
                                    )}
                                />
                            </Col>
                            <Col md-4>
                                <Controller
                                    control={control}
                                    name={FORM_FIELDS.ADDITIONAL_PHONE_OWNER}
                                    render={({ field }) => (
                                        <SelectInput
                                            {...field}
                                            label={LABELS[FORM_FIELDS.ADDITIONAL_PHONE_OWNER]}
                                            withCrossIcon={false}
                                            options={additionalPhoneOwnerOptions}
                                            errorMessage={errors[FORM_FIELDS.ADDITIONAL_PHONE_OWNER]?.message}
                                        />
                                    )}
                                />
                            </Col>
                            {additionalPhoneFields.includes(FORM_FIELDS.ADDITIONAL_PHONE_OWNER_FIO) && (
                                <Col md-4>
                                    <Controller
                                        control={control}
                                        name={FORM_FIELDS.ADDITIONAL_PHONE_OWNER_FIO}
                                        render={({ field: { onBlur, ...field } }) => (
                                            <FullNameSuggest
                                                input={{
                                                    ...field,
                                                    label: LABELS[FORM_FIELDS.ADDITIONAL_PHONE_OWNER_FIO],
                                                    errorMessage:
                                                        errors[FORM_FIELDS.ADDITIONAL_PHONE_OWNER_FIO]?.message,
                                                }}
                                                onSelect={onSelectFullName}
                                                onChange={field.onChange}
                                                onBlur={onBlur}
                                            />
                                        )}
                                    />
                                </Col>
                            )}
                        </Row>
                    </Rows>
                </AccordionItem>
            </Accordion>
            <StepButtons
                continueButtonDisabled={isValidating}
                onReturnButtonClick={goBack}
                className={commonStyles['step-buttons-wrapper']}
            />
        </Layout>
    );
});

PassportStepView.displayName = 'PassportStepView';
